import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import i18n, { InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

import 'dayjs/locale/de';
import 'dayjs/locale/pl';
import 'dayjs/locale/fr';
import 'dayjs/locale/tr';
import 'dayjs/locale/cs';
import 'dayjs/locale/uk';
import 'dayjs/locale/it';
import 'dayjs/locale/es';

//Keep the order because it is used in the LanguageSelector component to this the LanguageSelectorButton component in this order

export const SUPPORTED_LNGS = [
	'de',
	'en',
	'cs',
	'fr',
	'es',
	'it',
	'pl_PL',
	'tr',
	'uk_UA',
] as const;

export type SupportedLngs = typeof SUPPORTED_LNGS[number];

export const NS = [
	'booking',
	'changePassword',
	'checkIn',
	'common',
	'discardChangesPrompt',
	'docsUpload',
	'downloadInvoices',
	'dticket',
	'errors',
	'forgotPassword',
	'home',
	'logIn',
	'manageBillingAddress',
	'navigation',
	'notifications',
	'onboarding',
	'payment',
	'permissions',
	'personalDetails',
	'profile',
	'pushToApp',
	'registration',
	'routeDetails',
	'searchForm',
	'searchResults',
	'specialNeeds',
	'dticketSubscriptionPause',
	'subscribeDTicket',
	'dticketSubscriptionEditPause',
	'dticketResumeSubscription',
	'editPaymentMethod',
	'tickets',
	'ticketshop',
	'userDTicket',
	'validation',
	'vehicleCheck',
	'verify',
	'verifyReminder',
	'welcome',
] as const;

export type TNamespace = typeof NS[number];

export interface I18nConfig {
	/**
	 * Optional prefix to be applied to the loadPath for language files.
	 * Default is '' (empty string).
	 * Example: '/my-app' would result in '/my-app/locales/{{lng}}/{{ns}}.json'
	 */
	pathPrefix?: string;

	/**
	 * Optional array of namespaces to be used from the available NS list.
	 * If not provided, all available namespaces will be used.
	 * Only values from the NS const are allowed.
	 */
	namespaces?: readonly TNamespace[];

	/**
	 * Additional i18next initialization options
	 */
	options?: Partial<InitOptions>;
}

/**
 * Initialize i18n with the given configuration
 *
 * @param config - Configuration options including pathPrefix and namespaces
 * @returns The initialized i18n instance
 */
export function initI18n(config: I18nConfig = {}) {
	const { pathPrefix = '', namespaces, options = {} } = config;

	// Use specified namespaces if provided, otherwise use all available namespaces
	const nsToUse = namespaces || NS;

	// Validate that all specified namespaces exist in the NS array
	if (namespaces) {
		for (const ns of namespaces) {
			if (!NS.includes(ns)) {
				console.warn(
					`Namespace '${ns}' is not defined in the available namespaces list.`
				);
			}
		}
	}

	console.log(
		'i18n initialized with config:',
		config,
		`${pathPrefix}locales/{{lng}}/{{ns}}.json`
	);

	i18n
		// load translation using xhr -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
		// learn more: https://github.com/i18next/i18next-http-backend
		.use(Backend)
		// detect user language
		// learn more: https://github.com/i18next/i18next-browser-languageDetector
		.use(LanguageDetector)
		// pass the i18n instance to react-i18next.
		.use(initReactI18next)
		.init({
			ns: nsToUse,
			interpolation: { escapeValue: false },
			nsSeparator: ':',
			debug: false,
			fallbackLng: 'de',
			supportedLngs: SUPPORTED_LNGS,
			defaultNS: 'common',
			backend: {
				loadPath: `${pathPrefix}locales/{{lng}}/{{ns}}.json`,
			},
			detection: {
				order: ['querystring', 'cookie', 'localStorage', 'navigator'],
			},
			...options,
		});

	// Set up language change handler
	i18n.on('languageChanged', function (lng) {
		dayjs.locale(lng.substring(0, 2));
	});

	return i18n;
}

// Export i18n instance for use in other modules
// Note: You must call initI18n() to initialize i18n before using it
export default i18n;

//TODO v3 -> v4 https://i18next.github.io/i18next-v4-format-converter-web/
